import {Bond} from "../../models/Bond";
import {useEffect, useMemo, useState} from "react";
import GatewayService from "../../service/GatewayService";
import StatUtil from "../../util/StatUtil";

export type BondWithYTMs = Bond & {
    ytms: {
        last: number;
        bid: number;
        ask: number;
        low: number;
        high: number;
        avg: number;
    }
}

export const useBonds = () => {
    const [bonds, setBonds] = useState<Bond[]>([]);
    const [sortConfig, setSortConfig] = useState<{ key: string; direction: 'ascending' | 'descending' } | null>(null);
    const [tradedTodayOnly, setTradedTodayOnly] = useState(false);
    const [ytmThreshold, setYtmThreshold] = useState(0);
    const [instrumentType, setInstrumentType] = useState("");
    const [buyersAvailable, setBuyersAvailable] = useState(false);
    const [sellersAvailable, setSellersAvailable] = useState(false);
    const [highYieldOnly, setHighYieldOnly] = useState(false);

    const refreshBonds = () => {
        if (highYieldOnly) {
            GatewayService.getHighYieldBonds().then(setBonds);
        } else {
            GatewayService.getBonds().then(setBonds);
        }
    };

    useEffect(() => {
        refreshBonds();
    }, [highYieldOnly]);

    const processedBonds = useMemo(() => {
        let retVal =  bonds.filter(bond => {
            if (instrumentType && bond.genInfo.instrument_type !== instrumentType) {
                return false;
            }
            if (buyersAvailable && !bond.tick.buy?.[0]?.price) {
                return false;
            }
            if (sellersAvailable && !bond.tick.sell?.[0]?.price) {
                return false;
            }
            if (tradedTodayOnly) {
                if (!bond.tick.volume_traded || bond.tick.volume_traded === 0) return false;
                const today = new Date();
                today.setHours(0, 0, 0, 0);
                const tradeDate = new Date(bond.tick.last_trade_time);
                if (tradeDate < today) {
                    return false;
                }
            }
            return true;
        }).map(bond => ({
            ...bond,
            ytms: {
                last: StatUtil.calculateYTMfromBond(bond, bond.tick.last_price),
                bid: StatUtil.calculateYTMfromBond(bond, bond.tick.buy?.[0]?.price),
                ask: StatUtil.calculateYTMfromBond(bond, bond.tick.sell?.[0]?.price),
                low: StatUtil.calculateYTMfromBond(bond, bond.tick.ohlc.low),
                high: StatUtil.calculateYTMfromBond(bond, bond.tick.ohlc.high),
                avg: StatUtil.calculateYTMfromBond(bond, bond.tick.average_traded_price),
            }
        })).filter(bond => {
            if (ytmThreshold > 0) {
                const options = [
                    bond.ytms.last,
                    bond.ytms.bid,
                    bond.ytms.ask,
                    bond.ytms.low,
                ];
                if (Math.max(...options) <= ytmThreshold) {
                    return false;
                }
            }
            return true;
        }).sort((a, b) => {
            const getSortValue = (bond: BondWithYTMs, key: string): any => {
                switch (key) {
                    case 'genInfo.tradingsymbol': return bond.genInfo.tradingsymbol;
                    case 'genInfo.coupon': return bond.genInfo.coupon;
                    case 'genInfo.expiry': return bond.genInfo.expiry;
                    case 'genInfo.exchange': return bond.genInfo.exchange;
                    case 'tick.last_price': return bond.tick.last_price;
                    case 'tick.ohlc.high': return bond.tick.ohlc.high;
                    case 'tick.ohlc.low': return bond.tick.ohlc.low;
                    case 'tick.last_trade_time': return bond.tick.last_trade_time;
                    case 'tick.timestamp': return bond.tick.timestamp;
                    case 'tick.volume_traded': return bond.tick.volume_traded;
                    case 'tick.average_traded_price': return bond.tick.average_traded_price;
                    case 'bid': return bond.tick.buy?.[0]?.price;
                    case 'ask': return bond.tick.sell?.[0]?.price;
                    case 'ytm_last': return bond.ytms.last;
                    case 'ytm_bid': return bond.ytms.bid;
                    case 'ytm_ask': return bond.ytms.ask;
                    case 'ytm_low': return bond.ytms.low;
                    case 'ytm_high': return bond.ytms.high;
                    case 'ytm_avg': return bond.ytms.avg;
                    default: return null;
                }
            };
            if (sortConfig == null) return 0
            const aValue = getSortValue(a, sortConfig.key);
            const bValue = getSortValue(b, sortConfig.key);

            if (aValue === null || aValue === undefined) return 1;
            if (bValue === null || bValue === undefined) return -1;

            if (aValue < bValue) {
                return sortConfig.direction === 'ascending' ? -1 : 1;
            }
            if (aValue > bValue) {
                return sortConfig.direction === 'ascending' ? 1 : -1;
            }
            return 0;
        })
        return retVal;
    }, [bonds, sortConfig, tradedTodayOnly, ytmThreshold, instrumentType, buyersAvailable, sellersAvailable]);

    const requestSort = (key: string) => {
        let direction: 'ascending' | 'descending' = 'ascending';
        if (sortConfig && sortConfig.key === key && sortConfig.direction === 'ascending') {
            direction = 'descending';
        }
        setSortConfig({ key, direction });
    };

    return {
        processedBonds,
        requestSort,
        setTradedTodayOnly,
        tradedTodayOnly,
        setYtmThreshold,
        ytmThreshold,
        instrumentType,
        setInstrumentType,
        buyersAvailable,
        setBuyersAvailable,
        sellersAvailable,
        setSellersAvailable,
        refreshBonds,
        highYieldOnly,
        setHighYieldOnly,
    }
}
